import { V2Badge } from '@sakhtar/design-system/react';
import type { DrillAction, TableBlock, TableCellValue } from '../types.js';

export interface ResultTableProps {
  readonly table: TableBlock;
  /** Entity-name click → one layer deeper. */
  readonly onDrill: (action: DrillAction) => void;
  /** Record-number click → moves the context panel focus (newest answer only). */
  readonly onFocusRecord: (label: string) => void;
  /** Whether record numbers are links (only the newest answer's are). */
  readonly recordsInteractive: boolean;
  readonly disabled: boolean;
}

function Cell({
  cell,
  onDrill,
  onFocusRecord,
  recordsInteractive,
  disabled,
}: { cell: TableCellValue } & Omit<ResultTableProps, 'table'>) {
  if (typeof cell === 'string') return <>{cell}</>;
  switch (cell.kind) {
    case 'link':
      return (
        <button
          type="button"
          className="ai-link"
          disabled={disabled}
          onClick={() => onDrill(cell.action)}
        >
          {cell.text}
        </button>
      );
    case 'record':
      return recordsInteractive ? (
        <button
          type="button"
          className="ai-link ai-mono ltr"
          onClick={() => onFocusRecord(cell.text)}
        >
          {cell.text}
        </button>
      ) : (
        <span className="ai-mono ltr">{cell.text}</span>
      );
    case 'badge':
      return <V2Badge tone={cell.tone}>{cell.text}</V2Badge>;
  }
}

/**
 * Component 4 — compact result table; entity names drill down; footer «نمایش همه (N …)».
 * On mobile (frame M2) the same markup is restyled by CSS into one stacked card per
 * row: the first cell is the card title, every other cell a label / value pair read
 * from its `data-label` — the entity links stay real buttons.
 */
export function ResultTable({ table, ...cellProps }: ResultTableProps) {
  return (
    <div className="ai-block ai-table">
      <div className="sk-table-scroll">
        <table className="sk-table sk-table--compact">
          {table.caption ? <caption className="ai-table__caption">{table.caption}</caption> : null}
          <thead>
            <tr>
              {table.columns.map((column) => (
                <th key={column.key} className={column.align === 'num' ? 'num' : undefined}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row) => (
              <tr key={row.id}>
                {table.columns.map((column) => {
                  const cell = row.cells[column.key] ?? '';
                  const isRecord = typeof cell === 'object' && cell.kind === 'record';
                  return (
                    <td
                      key={column.key}
                      data-label={column.label}
                      className={column.align === 'num' ? 'num' : isRecord ? 'ltrcell' : undefined}
                    >
                      <Cell cell={cell} {...cellProps} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {table.footer ? (
            <tfoot>
              <tr>
                {table.columns.map((column) => (
                  <td
                    key={column.key}
                    data-label={column.label}
                    className={column.align === 'num' ? 'num' : undefined}
                  >
                    <strong>{table.footer?.[column.key] ?? ''}</strong>
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      {table.moreLabel ? (
        <div className="ai-table__more">
          <button
            type="button"
            className="ai-link"
            disabled
            title="در نسخهٔ نمایشی فعال نیست؛ فهرست کامل به داده واقعی نیاز دارد"
          >
            {table.moreLabel}
          </button>
        </div>
      ) : null}
    </div>
  );
}
